import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import * as moment from 'moment-timezone';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { GasprecioService } from 'src/app/modules/gasprecio/services/gasprecio.service';
import { SharedService } from 'src/app/modules/gasprecio/services/shared.service';

@Component({
  selector: 'app-select-week',
  templateUrl: './select-week.component.html',
  styleUrls: ['./select-week.component.scss']
})
export class SelectWeekComponent implements OnInit, OnDestroy {

  public items: any[] = [];
  public week: any = [];
  public weekSelected;
  public ngxDisabled = true;
  // Etiqueta OPCIONAL para el periodo ACTUAL (la opción que contiene hoy, ver
  // getWeeksSchedule):
  // renombra solo el TEXTO visible de esa opción (p. ej. "Semana actual") sin
  // tocar su value — el value sigue siendo el nombre original del backend, que
  // es lo que viaja por SharedService y mantiene sincronizadas las demás
  // instancias/vistas. Pedida para los filtros sin título del flujo MOBICO
  // (task 2026-07-10): sin label arriba, la opción debe autoexplicar el filtro.
  @Input() currentWeekLabel?: string;
  // Flujo de pipas (revisión 08-oct "ponerlo todo en un filtro solo"): la misma
  // lista de semanas termina con "Mes actual" y "Mes anterior". Elegir un mes
  // NO toca la semana compartida: publica el periodo (sharedPipesPeriod) y el
  // control muestra el mes; elegir una semana regresa el periodo a 'week'.
  @Input() periodOptions = false;
  // Flujo de pipas (Daniel 14-oct: «tiene que venir por default Semana actual y
  // Semana sig»): opción que abarca la semana actual y la siguiente, y es la
  // predeterminada mientras el usuario no elija otra en esa vista. Solo la
  // tienen los selectores de Solicitud de pipas; las demás vistas no la ven.
  @Input() twoWeeksOption = false;
  // Flujo de pipas (revisión 16-oct, prioridad de Emiliano): el filtro de
  // semanas es un selector MÚLTIPLE: todas, ninguna o las que quiera el usuario.
  // Publica la lista por sharedWeeksSelected y, para las vistas que siguen
  // leyendo UNA semana, el intervalo que abarca de la primera a la última.
  // Default: semana actual + las dos siguientes («dos semanas y media», jefe 19-oct;
  // antes actual y siguiente, Daniel 14-oct), mientras no se elija otra cosa.
  @Input() multiWeeks = false;
  // Sugerencias de la semana (Edey 16-oct): solo las semanas que el motor sugiere
  // (actual, siguiente y, desde el 19-oct, la de después; no se guardan
  // sugerencias de semanas pasadas). Default actual + siguiente; la tercera se
  // marca a mano. Las semanas elegidas en otras bandas fuera de ese rango se
  // respetan: este selector solo mueve las suyas dentro de la lista compartida.
  @Input() onlyCurrentAndNext = false;
  /** Opciones del multiselect (mismo formato que app-multiselect-product). */
  public multiItems: { item_id: string; item_text: string }[] = [];
  public multiSelected: { item_id: string; item_text: string }[] = [];
  public multiSettings = {
    singleSelection: false,
    idField: 'item_id',
    textField: 'item_text',
    enableCheckAll: true,
    selectAllText: 'Todas las semanas',
    unSelectAllText: 'Ninguna',
    itemsShowLimit: 1,
    allowSearchFilter: false,
    maxHeight: 200,
  };
  /** Última lista publicada en modo multi, para no re-publicar lo mismo. */
  private multiSig = '';
  weeks$?: Subscription;
  static readonly TWO_WEEKS = '__two_weeks';
  /** El usuario ya eligió una semana en Solicitud de pipas en esta sesión. */
  private static pickedInPipes = false;
  private period = 'week';
  /** Semana real (value del back) mientras el control muestra un mes. */
  private weekValue: any = '';
  week$: Subscription;
  weekSelected$: Subscription;
  period$?: Subscription;

  constructor(
    public sanitizer: DomSanitizer,
    private gasprecioService: GasprecioService,
    private sharedService: SharedService
  ) {
    this.week$ = sharedService.sharedWeek.subscribe((week: string) => {
      if (week !== this.weekValue) {
        this.weekValue = week;
      }
      if (this.period === 'week' && week !== this.week && !this.multiWeeks) {
        this.week = week;
      }
    });
    // Modo multi: las demás instancias (Sugerencias, Solicitudes, Cronograma)
    // se sincronizan por la lista, no por la semana única.
    this.weeks$ = sharedService.sharedWeeksSelected.subscribe((weeks: any[] | null) => {
      if (!this.multiWeeks || !weeks) { return; }
      const sig = weeks.map((w: any) => w.value).join('|');
      if (sig !== this.multiSig) {
        this.multiSig = sig;
        this.multiSelected = this.toMultiSelected(weeks);
      }
    });
    this.period$ = sharedService.sharedPipesPeriod.subscribe((period: string) => {
      if (period === this.period) { return; }
      this.period = period;
      // Solo los selectores con opciones de mes muestran el mes; los demás
      // siguen enseñando la semana (Sugerencias), pero conocen el periodo para
      // regresarlo a 'week' cuando se elige una semana en ellos.
      if (this.periodOptions && !this.multiWeeks) {
        this.week = period === 'week' ? this.weekValue : `__${period}`;
      }
    });

    this.weekSelected$ = this.sharedService.sharedWeekSelected.subscribe(weekSelected => {
      if (weekSelected !== this.weekSelected) {
        this.weekSelected = weekSelected;
      }
    });
  }

  public style(data: string): SafeStyle {
    return this.sanitizer.bypassSecurityTrustStyle(data);
  }

  ngOnInit(): void {
    this.getWeeksSchedule();
  }

  ngOnDestroy() {
    this.week$.unsubscribe();
    this.weekSelected$.unsubscribe();
    this.period$?.unsubscribe();
    this.weeks$?.unsubscribe();
  }

  getWeeksSchedule() {
    this.gasprecioService.getWeeksSchedule().subscribe((data: any) => {
      const weeks: any = data;
      // El backend no garantiza el orden de las semanas: se ordenan acá de forma
      // determinista por fecha de inicio, DESCENDENTE (más reciente/futura
      // primero). La opción "actual" se busca después por CONTENIDO (contiene
      // hoy), no por posición. Defensivo: si a algún item le faltara
      // interval/start_date, no rompe el sort (va al final, orden estable).
      const sortedWeeks = [...weeks].sort((a: any, b: any) => {
        const aTime = a?.interval?.start_date ? new Date(a.interval.start_date).getTime() : NaN;
        const bTime = b?.interval?.start_date ? new Date(b.interval.start_date).getTime() : NaN;
        if (isNaN(aTime) && isNaN(bTime)) { return 0; }
        if (isNaN(aTime)) { return 1; }
        if (isNaN(bTime)) { return -1; }
        return bTime - aTime;
      });
      sortedWeeks.forEach((element: any) => {
        this.items.push({ name: element['name'], value: element['name'], interval: element['interval'] });
      });
      // Opción "actual" = la que CONTIENE la fecha de hoy, buscada por CONTENIDO
      // y no por posición (fix 2026-07-14): con /weeks de semanas sueltas la
      // actual era items[1], pero cuando el backend regresa bloques de 2 semanas
      // la actual pasa a items[0] — el default posicional caía en el bloque
      // PASADO y las vistas cargaban vacías hasta refrescar. end_date llega como
      // inicio del último día, por eso el margen de +24h en el límite superior.
      const today = Date.now();
      let currentIdx = this.items.findIndex((it: any) => {
        const start = it?.interval?.start_date ? new Date(it.interval.start_date).getTime() : NaN;
        const end = it?.interval?.end_date ? new Date(it.interval.end_date).getTime() : NaN;
        return !isNaN(start) && !isNaN(end) && start <= today && today < end + 86400000;
      });
      if (currentIdx === -1) {
        currentIdx = this.items.length > 1 ? 1 : 0;
      }
      // Solo el texto visible: el value queda intacto (ver comentario del @Input).
      if (this.currentWeekLabel && this.items[currentIdx]) {
        this.items[currentIdx]['name'] = this.currentWeekLabel;
      }
      // Semana actual + siguiente: la siguiente es la que va justo antes en la
      // lista (orden descendente). Sin siguiente publicada no hay opción.
      const next = currentIdx > 0 ? this.items[currentIdx - 1] : null;
      if (this.multiWeeks) {
        // Flujo de pipas: «dos semanas y media» (jefe, 19-oct: «si estamos en
        // jueves tengo que mostrar medio, uno y uno»). /weeks solo publica hasta
        // la siguiente, así que la de después se arma aquí, solo en este modo.
        let afterNext = currentIdx > 1 ? this.items[currentIdx - 2] : null;
        if (!afterNext && next) {
          afterNext = this.weekAfter(next);
          this.items.unshift(afterNext);
          currentIdx++;
        }
        this.initMulti(currentIdx, next, afterNext);
        return;
      }
      const twoWeeks = this.twoWeeksOption && next?.interval?.end_date && this.items[currentIdx]?.interval?.start_date
        ? {
          name: 'Actual y siguiente',
          value: SelectWeekComponent.TWO_WEEKS,
          interval: { ...this.items[currentIdx]['interval'], end_date: next.interval.end_date },
        }
        : null;
      if (twoWeeks) {
        this.items.splice(currentIdx, 0, twoWeeks);
        currentIdx++;
      }
      const currentValue = this.items[currentIdx]['value'];
      const shown = Array.isArray(this.week) ? this.week[0] : this.week;
      if (twoWeeks && !SelectWeekComponent.pickedInPipes && (shown === '' || shown === currentValue || shown === SelectWeekComponent.TWO_WEEKS)) {
        this.week = SelectWeekComponent.TWO_WEEKS;
        this.weekValue = this.week;
        this.weekSelected = twoWeeks.interval;
        this.sharedService.nextWeekSelected = this.weekSelected;
        this.sharedService.nextWeek = this.week;
      } else if (this.week === '' || this.weekSelected === '' || !this.items.some((it: any) => it.value === shown)) {
        // Sin semana, o con una que este selector no tiene (p. ej. la de dos
        // semanas de Solicitud de pipas al abrir otra vista): la semana actual.
        this.week = [currentValue];
        this.weekSelected = this.items[currentIdx]['interval'];
        this.sharedService.nextWeekSelected = this.weekSelected;
        if (shown === SelectWeekComponent.TWO_WEEKS) {
          this.weekValue = currentValue;
          this.sharedService.nextWeek = currentValue;
        }
      }
      // Los meses van ARRIBA, en el mismo orden que las semanas (de lo más
      // reciente hacia atrás): mes actual, mes anterior, y luego las semanas.
      // Se insertan después de resolver currentIdx, que es posicional.
      if (this.periodOptions) {
        this.items.unshift(
          { name: 'Mes actual', value: '__month', interval: null },
          { name: 'Mes anterior', value: '__prev_month', interval: null },
        );
      }
      // Si el periodo ya estaba en un mes (otra banda lo eligió), mostrarlo.
      if (this.periodOptions && this.period !== 'week') {
        this.weekValue = this.week;
        this.week = `__${this.period}`;
      }
      this.ngxDisabled = false;
    });
  }

  /** Semana inmediatamente posterior a `week`, con el mismo formato que publica
   *  /weeks (nombre «D MMM - D MMM» en español y fechas de México). */
  private weekAfter(week: any): any {
    const tz = 'America/Mexico_City';
    const start = moment.tz(week.interval.start_date, tz).add(7, 'days');
    const end = moment.tz(week.interval.end_date, tz).add(7, 'days');
    const name = `${this.dayMonth(start)} - ${this.dayMonth(end)}`;
    return { name, value: name, interval: { start_date: start.toISOString(), end_date: end.toISOString() } };
  }

  /** «26 oct.», como moment en español (sin cambiar el locale global). */
  private dayMonth(m: moment.Moment): string {
    const months = ['ene.', 'feb.', 'mar.', 'abr.', 'may.', 'jun.', 'jul.', 'ago.', 'sep.', 'oct.', 'nov.', 'dic.'];
    return `${m.date()} ${months[m.month()]}`;
  }

  /** Modo multi: solo las semanas (los atajos «Todas las semanas» / «Ninguna»
   *  los pone el propio multiselect en su primera fila). Default actual + las
   *  dos siguientes (dos semanas y media, 19-oct) salvo que ya haya una lista
   *  elegida en esta sesión; Sugerencias (onlyCurrentAndNext) actual + siguiente. */
  private initMulti(currentIdx: number, next: any, afterNext: any = null): void {
    const thisWeek = this.items[currentIdx];
    if (this.onlyCurrentAndNext) {
      this.items = [afterNext, next, thisWeek].filter((it) => !!it);
    }
    this.multiItems = this.items.map((it: any) => ({ item_id: it.value, item_text: it.name }));
    this.ngxDisabled = false;
    // Otra instancia (o una visita anterior) ya eligió semanas: se muestran esas.
    // La suscripción del constructor no sirve aquí: corre antes de que llegue el
    // @Input multiWeeks, así que la lista vigente se lee del servicio.
    const current = this.sharedService.currentWeeksSelected;
    if (current) {
      this.multiSig = current.map((w: any) => w.value).join('|');
      this.multiSelected = this.toMultiSelected(current);
      return;
    }
    // Sugerencias abre con actual + siguiente (14 días); Solicitudes y Cronograma con las tres.
    const values = [thisWeek?.value, next?.value, this.onlyCurrentAndNext ? null : afterNext?.value].filter((v) => v != null);
    this.publishMulti(values);
  }

  /** Lo elegido en orden cronológico (la lista publicada ya viene así): el
   *  control enseña la primera («Semana actual») y «+N» con las demás. */
  private toMultiSelected(weeks: any[]): { item_id: string; item_text: string }[] {
    return weeks
      .map((w: any) => this.multiItems.find((it) => it.item_id === w.value))
      .filter((it) => !!it) as { item_id: string; item_text: string }[];
  }

  /** Cambio desde el control (casilla, «Todas las semanas» o «Ninguna»). Se
   *  engancha a los eventos del multiselect, no a ngModelChange: su writeValue
   *  re-emite el cambio y publicar las semanas lo reescribe (ciclo infinito). */
  changeWeeks(selected: any[]): void {
    const values: string[] = (selected || []).map((o: any) => o?.item_id).filter((v: any) => v != null);
    SelectWeekComponent.pickedInPipes = true;
    this.publishMulti(values);
  }

  private publishMulti(values: string[]): void {
    // Semanas ya elegidas que este selector no lista (fuera de actual/siguiente):
    // se conservan tal cual, solo cambian las que sí están en su lista.
    const outside = (this.sharedService.currentWeeksSelected || [])
      .filter((w: any) => !this.items.some((it: any) => it.value === w.value));
    const chosen = this.items
      .filter((it: any) => it.interval && values.includes(it.value))
      .map((it: any) => ({ value: it.value, name: it.name, start_date: it.interval.start_date, end_date: it.interval.end_date }))
      .concat(outside)
      .sort((a: any, b: any) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime());
    const sig = chosen.map((c: any) => c.value).join('|');
    const sameAsBefore = sig === this.multiSig && this.period === 'week';
    this.multiSig = sig;
    this.multiSelected = this.toMultiSelected(chosen);
    if (sameAsBefore) { return; }
    // Las semanas elegidas mandan: si otra banda había dejado un mes, se regresa
    // a modo semana para que todas las tablas lean la misma lista.
    if (this.period !== 'week') {
      this.period = 'week';
      this.sharedService.nextPipesPeriod = 'week';
    }
    // Intervalo que abarca todo lo elegido (lo leen las vistas de una semana);
    // sin semanas, la semana en curso: las tablas filtran local a «nada».
    const first = chosen[0];
    const last = chosen[chosen.length - 1];
    const envelope = first
      ? { start_date: first.start_date, end_date: last.end_date }
      : { start_date: new Date(new Date().setHours(0, 0, 0, 0)).toISOString(), end_date: new Date(new Date().setHours(0, 0, 0, 0)).toISOString() };
    this.weekSelected = envelope;
    this.weekValue = first ? first.value : '';
    this.sharedService.nextWeeksSelected = chosen;
    this.sharedService.nextWeekSelected = envelope;
    this.sharedService.nextWeek = this.weekValue;
  }

  changeWeek(event: any) {
    this.week = event;
    if (this.twoWeeksOption) {
      SelectWeekComponent.pickedInPipes = true;
    }
    if (this.periodOptions && typeof event === 'string' && event.startsWith('__') && event !== SelectWeekComponent.TWO_WEEKS) {
      // Mes: solo publica el periodo; la semana compartida se queda como está.
      this.period = event.slice(2);
      this.sharedService.nextPipesPeriod = this.period;
      return;
    }
    // Una semana elegida en CUALQUIER banda (también Sugerencias, sin opciones
    // de mes) regresa Solicitudes y Cronograma a modo semana: un selector mueve
    // todas las tablas.
    if (this.period !== 'week') {
      this.period = 'week';
      this.sharedService.nextPipesPeriod = 'week';
    }
    this.weekValue = event;
    this.items.forEach((element: any) => {
      // Match por value (no name): con currentWeekLabel el name visible de la
      // semana actual ya no coincide con lo que guarda el ngModel (el value).
      // Sin la etiqueta, value === name, así que el comportamiento es idéntico.
      if (element['value'] === this.week) {
        this.weekSelected = element['interval'];
        this.sharedService.nextWeekSelected = this.weekSelected;
      }
    });
    this.sharedService.nextWeek = this.week;
  }
}
